import {
    Button,
    Box,
    Text,
    ThemeSpaceVariable,
    useDisclosure,
    Flex,
} from "@caspeco/casper-ui-library";
import { useConfirmWaitListBooking } from "api/api-hooks/use-confirm-wait-list";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { usePaymentTerminalMyBooking } from "api/api-hooks/use-payment-terminal";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import BookingRequestRespondModal from "components/booking-request/booking-request-respond-modal";
import { PaymentMethodField } from "components/payment-field/payment-field";
import { getDefaultPaymentMethod } from "helpers/payment-helper";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getSearchParams } from "search-params";
import { ContactInfoForm } from "store/store-types";
import {
    hasUnhandledNoShowLink,
    hasUnhandledPaymentEventLink,
    hasUnpaidPrepayment,
    isNoShowLink,
    isPaymentEventExpired,
    isWaitList,
} from "utils/booking";

const BookingActionButton = () => {
    const { t } = useTranslation();
    const { data: webBooking } = useWebBooking();
    const { mutate: confirmMutate, isPending: confirmIsLoading } = useConfirmWaitListBooking();
    const paymentTerminal = usePaymentTerminalMyBooking();
    const { isPaymentLink } = getSearchParams();
    const state = useBookingStateContext();
    const { data: settings } = useExternalBookingSettings();
    const {
        isOpen: isRespondModalOpen,
        onOpen: onRespondModalOpen,
        onClose: onRespondModalClose,
    } = useDisclosure();
    const inquiryProposal = webBooking?.isInquiryProposal();

    const rule = webBooking?.webBookingRules?.at(0);
    const isPrepayment =
        rule?.hasPrepaymentRule() || (webBooking ? hasUnpaidPrepayment(webBooking) : false);

    const { control, watch, trigger } = useForm<ContactInfoForm>({
        mode: "onChange",
        defaultValues: {
            payMethod:
                settings && webBooking
                    ? getDefaultPaymentMethod(settings, isPrepayment, state)
                    : undefined,
        },
        reValidateMode: "onChange",
    });

    if (!webBooking) {
        return null;
    }

    if (!settings) {
        return null;
    }

    if (!webBooking.webBookingRules) {
        return null;
    }

    const availablePaymentMethods = settings.paymentMethods;
    const hasLinkPassed = isPaymentEventExpired(webBooking, isPaymentLink);

    const requestedAmount = webBooking.paymentEvents.at(-1)?.requestedAmount;
    const cardRegistration = rule?.hasNoShowRule() || hasUnhandledNoShowLink(webBooking);

    const getButtonText = () => {
        if (isPrepayment) {
            return t("payAmount", {
                amount: t("currency.onlyAmount", {
                    value: requestedAmount,
                }),
            });
        }
        if (cardRegistration) {
            return t("registerPaymentCard");
        }
        if (inquiryProposal) {
            return t("bookingRequest.respondToProposal");
        }
        return t("waitList.confirmBooking");
    };

    const isWaitListBooking = isWaitList(webBooking);
    const onActionClick = () => {
        if (inquiryProposal) {
            onRespondModalOpen();
            return;
        }

        const isPaymentLinkWithUnhandledPaymentEvent =
            hasUnhandledPaymentEventLink(webBooking, isPaymentLink) && !isWaitListBooking;
        const isWaitListWithPaymentRule = isWaitListBooking && rule?.hasAnyPaymentRule();

        if (isPaymentLinkWithUnhandledPaymentEvent || isWaitListWithPaymentRule) {
            paymentTerminal.mutate({
                state: { payment: { payMethod: watch("payMethod") } },
                isPaymentLink: isPaymentLinkWithUnhandledPaymentEvent,
                isWaitList: isWaitListWithPaymentRule,
            });
        } else {
            confirmMutate();
        }
    };

    const isNoShowPaymentLink = isNoShowLink(webBooking);
    const linkExpirationDate =
        webBooking.chargePaymentLinkExpirationDate?.format("YYYY-MM-DD HH:mm");
    const isPaymentMethodFieldVisible = (availablePaymentMethods?.length ?? 0) > 1 && isPrepayment;
    return (
        <>
            {isPaymentMethodFieldVisible && (
                <Box py={ThemeSpaceVariable.Medium}>
                    <PaymentMethodField control={control} trigger={trigger} />
                </Box>
            )}
            <Flex
                py={ThemeSpaceVariable.Medium}
                gap={ThemeSpaceVariable.Small}
                flexDirection={"column"}
                alignItems={"center"}
            >
                <Button
                    variant="primary"
                    isLoading={confirmIsLoading || paymentTerminal.isPending}
                    onClick={onActionClick}
                    isDisabled={hasLinkPassed}
                    w="full"
                    size="xl"
                >
                    {getButtonText()}
                </Button>
                {hasUnhandledPaymentEventLink(webBooking, isPaymentLink) &&
                    !isWaitListBooking &&
                    linkExpirationDate && (
                        <Text color={"inherit"} opacity="65%" justifyContent={"center"}>
                            {isNoShowPaymentLink
                                ? t("payment.cardRegistrationPossibleTo", {
                                      date: linkExpirationDate,
                                  })
                                : t("payment.prepaymentPossibleTo", { date: linkExpirationDate })}
                        </Text>
                    )}
            </Flex>
            <BookingRequestRespondModal isOpen={isRespondModalOpen} onClose={onRespondModalClose} />
        </>
    );
};

export default BookingActionButton;
